// Decode BOTH cells of a two-cell geometry compare (T30) in one batch, at the ON-DEMAND cap.
// Opening the dialog is itself an explicit "I chose to view this" action (spec Task 30), so unlike
// the peek's eager-then-load-full ladder (`useSpatialGeometryPreview.ts`), there is no intermediate
// "over-cap" step: a side over MAX_ONDEMAND_DECODE_BYTES goes straight to the terminal `too-large`
// state (B38/CD7) for THAT side only — the other side still decodes and renders. Reached only through
// the already-lazy `decodeSpatialBatch`/`loadSpatialDecoder` API (never the sync decode core).

import { useEffect, useRef, useState } from 'react';
import type { DecodedGeometry } from '../decode/types';
import type { SpatialCellInput } from '../../common/utils/spatialValueAccessor';
import type { PlanarTransform, SpatialMapFeature } from '../map/spatialMapTypes';
import { decodeSpatialBatch } from '../decode/decodeSpatialBatch';
import { loadSpatialDecoder } from '../decode/loadSpatialDecoder';
import { MAX_ONDEMAND_DECODE_BYTES } from '../decode/decodeLimits';
import { projectFeaturesForMap } from '../projection/sridProjection';
import { fetchDbSridDef } from '../projection/sridDefsProvider';
import type { SridDefCache } from '../projection/sridLadder';

export type SpatialCompareInput = {
  input: NonNullable<SpatialCellInput>;
  /** Windowed grids only: fetch the FULL untruncated value (T26 bounded batch). */
  fetchFullValue?: () => Promise<unknown>;
};

export type CompareSideState =
  | { status: 'loading' }
  | { status: 'ready'; geometry: DecodedGeometry; summary: string; value: unknown }
  | { status: 'too-large'; sizeMb: string }
  | { status: 'error'; message: string };

export type SpatialCompareDecodeState = {
  a: CompareSideState;
  b: CompareSideState;
  mapFeatureA: SpatialMapFeature | null; // reprojected to WGS84, or raw in planar mode
  mapFeatureB: SpatialMapFeature | null;
  planarTransform: PlanarTransform | null;
  projectionNote: string | null;
};

const LOADING: SpatialCompareDecodeState = {
  a: { status: 'loading' },
  b: { status: 'loading' },
  mapFeatureA: null,
  mapFeatureB: null,
  planarTransform: null,
  projectionNote: null,
};

const REASON_TEXT: Record<string, string> = {
  'unsupported-curve': 'Curved geometry is not supported yet.',
  'parse-error': 'Could not parse this geometry.',
  unrecognized: 'Unrecognized geometry format.',
};

// One SRID def cache per connection, shared with the peek/expanded-dialog decoders (T11/T12) so a
// DB lookup for a given SRID happens at most once per connection per session.
const sridCaches = new Map<string, SridDefCache>();
function sridCacheFor(connectionId: string | undefined): SridDefCache {
  if (!connectionId) return new Map();
  let cache = sridCaches.get(connectionId);
  if (!cache) {
    cache = new Map();
    sridCaches.set(connectionId, cache);
  }
  return cache;
}

function declKeyOf(input: NonNullable<SpatialCellInput>): string {
  const d = input.declaration;
  return d ? `${d.id}:${d.format}:${d.lngColumn ?? ''}:${d.axisOrder ?? ''}` : '';
}

function sideFromResult(
  result: Awaited<ReturnType<typeof decodeSpatialBatch>>[number]['result'],
  value: unknown,
  summarize: (g: DecodedGeometry) => string,
): { side: CompareSideState; feature: SpatialMapFeature | null } {
  if (result.ok) {
    return {
      side: { status: 'ready', geometry: result.geometry, summary: summarize(result.geometry), value },
      feature: { id: 0, geometry: result.geometry }, // id overwritten by the caller (a/b)
    };
  }
  if (result.reason === 'too-large') {
    return { side: { status: 'too-large', sizeMb: result.message }, feature: null };
  }
  return { side: { status: 'error', message: REASON_TEXT[result.reason] ?? 'Could not render geometry.' }, feature: null };
}

export function useSpatialCompareDecode(args: {
  a: SpatialCompareInput;
  b: SpatialCompareInput;
  enabled: boolean;
  connectionId?: string;
  database?: string;
}): SpatialCompareDecodeState {
  const { a, b, enabled, connectionId, database } = args;
  const [state, setState] = useState<SpatialCompareDecodeState>(LOADING);
  const runIdRef = useRef(0);
  // Fresh closures every render must not retrigger the decode — held in refs (mirrors
  // useSpatialGeometryPreview's fetchFullValueRef pattern).
  const aRef = useRef(a);
  aRef.current = a;
  const bRef = useRef(b);
  bRef.current = b;

  const declKeyA = declKeyOf(a.input);
  const declKeyB = declKeyOf(b.input);

  useEffect(() => {
    if (!enabled) return;
    const myId = ++runIdRef.current;
    setState(LOADING);

    void (async () => {
      const ia = aRef.current.input;
      const ib = bRef.current.input;
      const [fullA, fullB] = await Promise.all([
        ia.declaration || !aRef.current.fetchFullValue ? ia.value : aRef.current.fetchFullValue(),
        ib.declaration || !bRef.current.fetchFullValue ? ib.value : bRef.current.fetchFullValue(),
      ]);
      if (runIdRef.current !== myId) return;

      const results = await decodeSpatialBatch(
        [
          { key: 'a', engine: ia.engine, value: fullA, dataType: ia.dataType, declaration: ia.declaration },
          { key: 'b', engine: ib.engine, value: fullB, dataType: ib.dataType, declaration: ib.declaration },
        ],
        { maxBytesPerValue: MAX_ONDEMAND_DECODE_BYTES },
      );
      if (runIdRef.current !== myId) return;

      const { formatSpatialSummary, resolveSridMap } = await loadSpatialDecoder();
      if (runIdRef.current !== myId) return;

      const resA = results.find((r) => r.key === 'a')!.result;
      const resB = results.find((r) => r.key === 'b')!.result;
      const { side: sideA, feature: rawFeatureA } = sideFromResult(resA, fullA, formatSpatialSummary);
      const { side: sideB, feature: rawFeatureB } = sideFromResult(resB, fullB, formatSpatialSummary);
      if (rawFeatureA) rawFeatureA.id = 'a';
      if (rawFeatureB) rawFeatureB.id = 'b';

      const rawFeatures = [rawFeatureA, rawFeatureB].filter((f): f is SpatialMapFeature => f !== null);
      if (rawFeatures.length === 0) {
        setState({ a: sideA, b: sideB, mapFeatureA: null, mapFeatureB: null, planarTransform: null, projectionNote: null });
        return;
      }

      const fetchDef = connectionId
        ? (srid: number) => fetchDbSridDef(ia.engine, connectionId, database ?? '', srid)
        : undefined;
      const resolutions = await resolveSridMap(
        rawFeatures.map((f) => f.geometry.srid),
        { fetchDef, cache: sridCacheFor(connectionId) },
      );
      if (runIdRef.current !== myId) return;

      const projected = projectFeaturesForMap(rawFeatures, resolutions);
      setState({
        a: sideA,
        b: sideB,
        mapFeatureA: projected.features.find((f) => f.id === 'a') ?? null,
        mapFeatureB: projected.features.find((f) => f.id === 'b') ?? null,
        planarTransform: projected.planarTransform,
        projectionNote: projected.projectionNote,
      });
    })();

    return () => {
      runIdRef.current += 1; // cancel any in-flight decode on close/unmount
    };
  }, [enabled, a.input.engine, a.input.value, a.input.dataType, declKeyA, b.input.engine, b.input.value, b.input.dataType, declKeyB, connectionId, database]);

  return state;
}
